'use client'

import { useEffect, useState } from 'react'
import { pxToRem } from '@ui/style/muiTheme'
import UIFlex from '@components/ui/UIFlex'
import UIText from '@components/ui/UIText'
import RecommendButton from '@components/button/RecommendButton'
import { useExpressionMutation } from '@services/api/Article/Expression/mutation'
import { useAuthControl } from '@store/auth'
import useModal from '@components/layout/Modal'
import { useStatisticsSelectionAreaMutation } from '@services/api/Statistics/SelectionArea/mutation'

export interface RecommendButtonsProps {
	articleIdx: string
	articleLikeCnt: number
	articleDislikeCnt: number
	isLike: boolean
	isDislike: boolean
	areaIdx: number
}
const RecommendButtons = ({
	articleIdx,
	articleLikeCnt,
	articleDislikeCnt,
	isLike,
	isDislike,
	areaIdx,
}: RecommendButtonsProps) => {
	const [likeCnt, setLikeCnt] = useState<number>()
	const [dislikeCnt, setDisikeCnt] = useState<number>()
	const [likeActive, setLikeActive] = useState<boolean>()
	const [dislikeActive, setDislikeActive] = useState<boolean>()
	const { auth } = useAuthControl()
	const confirm = useModal()
	const { mutateAsync: expressionMutation } = useExpressionMutation()
	const { mutateAsync: selectionAreaMutation } = useStatisticsSelectionAreaMutation()

	const authAction = () => {
		if (!auth) {
			confirm.login()
			return false
		}
		return true
	}
	const likeAction = async () => {
		const { data } = await expressionMutation({ articleIdx, likeDislike: 'LIKE' })
		const { newsAnalysis } = window
		if (data) {
			if (likeCnt !== undefined) {
				setLikeCnt(likeActive ? likeCnt - 1 : likeCnt + 1)
			}
			setLikeActive(!likeActive)
			if (newsAnalysis) {
				newsAnalysis.init(likeActive ? 'unreaction' : 'reaction', articleIdx, 'like')
			}
		}
	}
	const dislikeAction = async () => {
		const { data } = await expressionMutation({ articleIdx, likeDislike: 'DISLIKE' })
		const { newsAnalysis } = window
		if (data) {
			if (dislikeCnt !== undefined) {
				setDisikeCnt(dislikeActive ? dislikeCnt - 1 : dislikeCnt + 1)
			}
			setDislikeActive(!dislikeActive)
			if (newsAnalysis) {
				newsAnalysis.init(dislikeActive ? 'unreaction' : 'reaction', articleIdx, 'unlike')
			}
		}
	}

	const selectionAreaAction = async () => {
		await selectionAreaMutation({ areaDiv: 'ARTICLE', areaIdx: areaIdx.toString() })
	}

	const handleLike = async () => {
		if (authAction()) {
			if (dislikeActive) {
				await dislikeAction()
			}
			likeAction()
			selectionAreaAction()
		}
	}
	const handleDislike = async () => {
		if (authAction()) {
			if (likeActive) {
				await likeAction()
			}
			dislikeAction()
			selectionAreaAction()
		}
	}
	useEffect(() => {
		setLikeCnt(articleLikeCnt)
		setDisikeCnt(articleDislikeCnt)
		setLikeActive(isLike)
		setDislikeActive(isDislike)
	}, [articleLikeCnt, articleDislikeCnt, isLike, isDislike])
	return (
		<UIFlex
			className="print-hide"
			alignItems="center"
			sx={{
				'&>*:not(:first-child)': {
					marginTop: pxToRem(16),
				},
			}}
		>
			<UIText fontSize={pxToRem(16)} lineHeight={pxToRem(22)} fontWeight={500}>
				이 기사를 추천합니다.
			</UIText>
			<UIFlex
				flexDirection="row"
				sx={{
					'&>*:not(:first-child)': {
						marginLeft: pxToRem(16),
					},
				}}
			>
				<RecommendButton
					buttonType="like"
					count={likeCnt ?? articleLikeCnt}
					onClick={handleLike}
					{...((likeActive ?? isLike) && { isPressed: true })}
				/>
				<RecommendButton
					buttonType="dislike"
					count={dislikeCnt ?? articleDislikeCnt}
					onClick={handleDislike}
					{...((dislikeActive ?? isDislike) && { isPressed: true })}
				/>
			</UIFlex>
		</UIFlex>
	)
}

export default RecommendButtons
